import type { ApolloError } from '@apollo/client';
import { useCallback } from 'react';

import { toast, useDeleteRecordModal } from '@/common/components';
import { useRevokeMcpConnectionMutation } from '@/common/graphql';
import { ApiUtils } from '@/common/utils';

import type { McpConnectionRow } from './McpConnectionTable';

/**
 * Asks for confirmation, revokes the connection and then lets the caller refresh its list.
 * Revoking cannot be undone: the application has to be signed in again.
 */
export const useRevokeMcpConnection = (onRevoked: () => unknown) => {
  const { openDeleteRecordModal } = useDeleteRecordModal();
  const [revokeMcpConnection] = useRevokeMcpConnectionMutation();

  return useCallback(
    (connection: McpConnectionRow) => {
      openDeleteRecordModal({
        title: `Revoke ${connection.clientName}?`,
        successButtonText: 'Revoke',
        onSuccess: async () => {
          try {
            await revokeMcpConnection({ variables: { id: connection.id } });
            toast.success('Connection revoked.');
            await onRevoked();
          } catch (error) {
            ApiUtils.handleApiError(error as ApolloError);
          }
        },
      });
    },
    [openDeleteRecordModal, revokeMcpConnection, onRevoked],
  );
};
